import { defineComponent as _defineComponent } from 'vue'
import {
  HedgingInstruments,
  ListQuery,
  PaginatedParams,
  Trade,
  closedBHTradeStatuses,
  openBHTradeStatuses,
  forwardInstruments,
  getFixedSideCcy,
} from 'ah-api-gateways';
import { VButton } from 'ah-common-lib/src/common/components';
import TradeListing from 'ah-trades/src/components/lists/TradeListing.vue';
import MaterialIcon from 'bd-common/src/components/common/MaterialIcon.vue';
import BDListBlock from 'bd-common/src/components/list/BDListBlock.vue';
import { computed, ref, watch } from 'vue';
import { useQueryParamRef } from 'ah-common-lib/src/helpers/useQueryParam';
import TradeDetailsModal from '@/app/components/clientTrades/TradeDetailsModal.vue';
import ClientTradesListFilter from '@/app/components/clientTrades/ClientTradesListFilter.vue';
import SearchFilter from 'bd-common/src/components/form/SearchFilter.vue';
import WarningSplash from 'bd-common/src/components/common/WarningSplash.vue';
import { DataTransforms } from 'ah-common-lib/src/listing/useManagedListingInterfaces';
import {
  clientTradesTableFields,
  forwardClientTradesTableFields,
} from '@/app/components/clientTrades/clientTradesListingTableFields';
import ChildTradesRowExpansion from '@/app/components/clientTrades/ChildTradesRowExpansion.vue';
import { TableConfiguration } from 'ah-common-lib/src/models';
import ViewTabsMenu from 'bd-common/src/components/common/menus/ViewTabsMenu.vue';
import { BhMenuItem } from 'bd-common/src/components/common/menus/BhBaseMenu.vue';
import CardCornerMenu from 'bd-common/src/components/common/menus/CardCornerMenu.vue';

type ViewMenuItem = (typeof viewMenuKeys)[number];

type TradeMenuItem = (typeof tradeMenuKeys)[number];


export default /*#__PURE__*/_defineComponent({
  __name: 'ClientTradesView',
  setup(__props) {

const viewMenuKeys = ['analytics', 'spots', 'forwards'] as const;

const tradeMenuKeys = ['openTrades', 'closedTrades'] as const;

const viewMenuItems: BhMenuItem[] = [
  { label: 'Analytics', value: 'analytics', icon: 'leaderboard' },
  { label: 'Spot Positions', value: 'spots', icon: 'format_list_bulleted' },
  { label: 'Forward Positions', value: 'forwards', icon: 'format_list_bulleted' },
];

const tradeMenuItems: BhMenuItem[] = [
  { label: 'Open Trades', value: 'openTrades', icon: 'radio_button_partial' },
  { label: 'Closed Trades', value: 'closedTrades', icon: 'task_alt' },
];

const sortAndPageParams: Partial<PaginatedParams> = {
  sort: 'createdAt',
  sortDirection: 'DESC',
};

const listBlock = ref<InstanceType<typeof BDListBlock> | null>(null);
const listing = ref<InstanceType<typeof TradeListing> | null>(null);

const selectedMenu = useQueryParamRef<ViewMenuItem>('tab', 'spots', {
  deserialize(param) {
    if (!viewMenuKeys.includes(param as any)) {
      return 'spots';
    }
    return param as ViewMenuItem;
  },
});

const selectedTradeMenu = useQueryParamRef<TradeMenuItem>('trade-tab', 'openTrades', {
  deserialize(param) {
    if (!tradeMenuKeys.includes(param as any)) {
      return 'openTrades';
    }
    return param as TradeMenuItem;
  },
});

const listConfig = computed(() => ({
  tableFields: selectedMenu.value === 'forwards' ? forwardClientTradesTableFields : clientTradesTableFields,
}));

const showOpenTrades = computed(() => selectedTradeMenu.value === 'openTrades');

const allowedStatuses = computed(() => (showOpenTrades.value ? openBHTradeStatuses : closedBHTradeStatuses));

const hedgingProduct = computed<HedgingInstruments[]>(() => {
  if (selectedMenu.value === 'spots') return [HedgingInstruments.FX_SPOT];
  if (selectedMenu.value === 'forwards') return forwardInstruments;
  return [];
});

const commonFilter = computed(() => ({
  hedgingProduct: hedgingProduct.value,
  isDrawdown: false,
}));

const menuLabel = computed(() => {
  return viewMenuItems.find((item) => item.value === selectedMenu.value)?.label;
});

const statusLabel = computed(() => {
  return tradeMenuItems.find((item) => item.value === selectedTradeMenu.value)?.label;
});

const tradeDetailsModal = ref<InstanceType<typeof TradeDetailsModal> | null>(null);

const selectedTrade = ref<Trade | null>(null);

const childlistConfig = computed<TableConfiguration>(() => {
  return {
    // We add an extra `.toggle-col` to child columns so columns count matches the parent's
    tableFields: [...listConfig.value.tableFields, { key: 'fakeToggleCol', header: '', class: 'toggle-col' }],
  };
});

function openTradeDetailsModal(item: Trade) {
  selectedTrade.value = item;
  setTimeout(() => {
    tradeDetailsModal.value?.modal?.show();
  });
}

function hasDrawdowns(trade: Trade) {
  return trade.hasDrawdowns;
}

const errorStateHeading = computed(() => `Error loading ${menuLabel.value}`);

const getTotalClientAmount = (trade: Trade) => {
  return getFixedSideCcy(trade).clientAmount;
};

const getUsageOfContractCurrency = (trade: Trade) => {
  return getFixedSideCcy(trade).currency;
};

const getUsageOfContractAmount = (trade: Trade) => {
  const rawValue = getFixedSideCcy(trade).clientAmount - getFixedSideCcy(trade).remainingClientAmount;
  return parseFloat(rawValue.toFixed(2));
};

const calculateUsageOfContract = (trade: Trade) => {
  return (getUsageOfContractAmount(trade) / getTotalClientAmount(trade)) * 100;
};

const formatUsageOfContract = (value: number) => {
  if (value >= 1e12) {
    // 1e12 is scientific notation for the number trillion

    const formattedValue = (value / 1e12).toFixed(1).replace(/\.0$/, '');
    return `${formattedValue}T`;
  } else if (value >= 1e9) {
    // 1e9 is scientific notation for the number billion

    const formattedValue = (value / 1e9).toFixed(1).replace(/\.0$/, '');
    return `${formattedValue}B`;
  } else if (value >= 1e6) {
    // 1e6 is scientific notation for the number million

    const formattedValue = (value / 1e6).toFixed(1).replace(/\.0$/, '');
    return `${formattedValue}M`;
  } else if (value >= 1e3) {
    // 1e3 is scientific notation for the number thousand
    const formattedValue = (value / 1e3).toFixed(1).replace(/\.0$/, '');
    return `${formattedValue}K`;
  } else {
    return `${value}`;
  }
};

watch(commonFilter, () => {
  listBlock.value?.managedListBlock.setFilter({});
});

watch(allowedStatuses, () => {
  listBlock.value?.managedListBlock.setFilter({});
  listing.value?.loadData();
});

function getCustomHeading(hasCustomFilters: boolean | undefined, filter: any) {
  if (hasCustomFilters) {
    return filter.query
      ? `We couldn’t find search results for ${filter.query}`
      : 'We couldn’t find results matching your filter(s)';
  } else {
    return `There are no ${statusLabel.value} for ${menuLabel.value}`;
  }
}

function getCustomSubHeading(hasCustomFilters: boolean | undefined, filter: any) {
  if (hasCustomFilters) {
    return filter.query ? 'Please clear or try another search' : 'Please clear or try other filters';
  } else {
    return 'Once SMEs book new trades, they will appear here';
  }
}

const dataTransforms: DataTransforms<any> = {
  queryTransform: (query) => {
    const out: ListQuery = { ...query };
    out.status = out.status?.length
      ? out.status.filter((i: any) => allowedStatuses.value.includes(i))
      : allowedStatuses.value;
    return out;
  },
};

return { __sfc: true,viewMenuKeys, tradeMenuKeys, viewMenuItems, tradeMenuItems, sortAndPageParams, listBlock, listing, selectedMenu, selectedTradeMenu, listConfig, showOpenTrades, allowedStatuses, hedgingProduct, commonFilter, menuLabel, statusLabel, tradeDetailsModal, selectedTrade, childlistConfig, openTradeDetailsModal, hasDrawdowns, errorStateHeading, getTotalClientAmount, getUsageOfContractCurrency, getUsageOfContractAmount, calculateUsageOfContract, formatUsageOfContract, getCustomHeading, getCustomSubHeading, dataTransforms, VButton, TradeListing, MaterialIcon, BDListBlock, TradeDetailsModal, ClientTradesListFilter, SearchFilter, WarningSplash, ChildTradesRowExpansion, ViewTabsMenu, CardCornerMenu }
}

})